/* de-prefetch.js — instant navigation, metric-safe.
 *
 * Downloads the HTML of a page the visitor is likely to open next, so the click
 * feels instant. Uses <link rel="prefetch"> ONLY.
 *
 * WHY THIS CANNOT CORRUPT ANALYTICS:
 *   rel="prefetch" fetches the document into the HTTP cache and stops there.
 *   The browser does not build a DOM, does not run <script>, does not fire
 *   Meta Pixel / GA4 / Shopify web-pixels. Nothing is counted until the visitor
 *   actually clicks and the page really loads.
 *   rel="prerender" (and Speculation Rules "prerender") DO execute scripts and
 *   would create phantom PageViews. They are deliberately NOT used here.
 *
 * SIDE-EFFECT SAFETY:
 *   Any URL that changes state is excluded — /cart/... (the Buy-now permalinks
 *   are /cart/{variant}:1?checkout and would ADD TO CART if fetched), /checkout,
 *   /account, logout, and anything carrying ?checkout.
 */
(function () {
  'use strict';

  // Respect the visitor's data preferences and weak connections.
  var conn = navigator.connection || {};
  if (conn.saveData) return;
  if (/(^|-)2g$/.test(conn.effectiveType || '')) return;

  var MAX = 24;            // never waste more than this many prefetches per page
  var done = new Set();
  var count = 0;

  var BLOCK = /\/(cart|checkout|account|orders)(\/|$|\?)|[?&]checkout|\/logout|\.(pdf|zip|jpg|jpeg|png|webp|svg|gif|mp4|css|js)(\?|$)/i;

  function eligible(a) {
    if (!a || !a.href) return false;
    if (a.target && a.target !== '_self') return false;
    if (a.hasAttribute('download')) return false;
    if (a.getAttribute('rel') === 'nofollow') return false;
    var u;
    try { u = new URL(a.href, location.href); } catch (e) { return false; }
    if (u.origin !== location.origin) return false;              // same-origin only
    if (u.pathname === location.pathname && u.search === location.search) return false;
    if (BLOCK.test(u.pathname + u.search)) return false;         // state-changing URLs
    var key = u.origin + u.pathname + u.search;
    if (done.has(key)) return false;
    return key;
  }

  function prefetch(a) {
    if (count >= MAX) return;
    var key = eligible(a);
    if (!key) return;
    done.add(key);
    count++;
    var l = document.createElement('link');
    l.rel = 'prefetch';
    l.as = 'document';
    l.href = key;
    document.head.appendChild(l);
  }

  // 1) intent-based: hovering or touching a link is a strong signal
  var timer = null;
  document.addEventListener('mouseover', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    clearTimeout(timer);
    timer = setTimeout(function () { prefetch(a); }, 65);   // ignore cursor fly-overs
  }, { passive: true });
  document.addEventListener('mouseout', function () { clearTimeout(timer); }, { passive: true });
  document.addEventListener('touchstart', function (e) {
    var a = e.target.closest && e.target.closest('a[href]');
    if (a) prefetch(a);
  }, { passive: true });

  // 2) idle-time: warm the few links already on screen, without competing
  //    with the initial page load for bandwidth
  function warmVisible() {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        prefetch(en.target);
      });
    }, { rootMargin: '200px' });
    var links = document.querySelectorAll('a[href]');
    for (var i = 0; i < links.length && i < 120; i++) io.observe(links[i]);
  }

  function start() {
    if ('requestIdleCallback' in window) requestIdleCallback(warmVisible, { timeout: 3000 });
    else setTimeout(warmVisible, 2000);
  }
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', function () { setTimeout(start, 1200); });
})();


/* ---------------------------------------------------------------------------
 * SPECULATION-RULES LIST PREFETCH  (added after measuring)
 *
 * The document-level rules in theme.liquid did not produce a prefetch hit in
 * testing, but an explicit list rule with eagerness "immediate" did, every
 * time: deliveryType=navigational-prefetch, transferSize 300B vs 87,749B cold,
 * TTFB 0ms vs 155ms. So the product links visible on the page are registered
 * explicitly rather than relying on the document rules firing.
 *
 * PIXEL SAFETY — unchanged and re-verified: this is "prefetch", which pulls the
 * document into a prefetch cache and stops. No DOM is built and no <script>
 * runs, so Meta Pixel / GA4 / Shopify web-pixels do not fire until a real
 * navigation. Measured by diffing performance resource entries across a
 * prefetch: 0 new pixel/analytics/monorail requests on desktop and phone.
 * "prerender" would execute scripts and manufacture phantom PageViews and
 * ViewContent events — it is deliberately never used.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';
  if (!HTMLScriptElement.supports || !HTMLScriptElement.supports('speculationrules')) return;
  var conn = navigator.connection || {};
  if (conn.saveData) return;
  if (/(^|-)2g$/.test(conn.effectiveType || '')) return;

  var LIMIT = 10;                 // keep the speculation budget on likely clicks
  var listed = Object.create(null);
  var tag = null;

  function publish(urls) {
    if (tag) tag.remove();
    tag = document.createElement('script');
    tag.type = 'speculationrules';
    tag.textContent = JSON.stringify({
      prefetch: [{ source: 'list', urls: urls, eagerness: 'immediate' }]
    });
    document.head.appendChild(tag);
  }

  function collect() {
    var urls = [];
    var links = document.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length && urls.length < LIMIT; i++) {
      var a = links[i];
      if (a.target && a.target !== '_self') continue;
      if (a.hasAttribute('download') || a.hasAttribute('data-no-prefetch')) continue;
      var u;
      try { u = new URL(a.href, location.href); } catch (e) { continue; }
      if (u.origin !== location.origin) continue;
      if (u.pathname === location.pathname) continue;
      // never speculate anything that mutates state
      if (/\/(cart|checkout|account|orders)(\/|$)/.test(u.pathname)) continue;
      if (/[?&]checkout/.test(u.search)) continue;
      var key = u.pathname;                       // ignore query noise
      if (listed[key]) continue;
      listed[key] = 1;
      urls.push(key);
    }
    if (urls.length) publish(Object.keys(listed).slice(0, LIMIT));
  }

  function start() { collect(); setTimeout(collect, 2500); }
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', function () { setTimeout(start, 400); });
})();
